/**
 * Pozycje nawigacji (§6 „Architektura informacji"). Zakres tego zadania (etap 5 zadanie 1)
 * ogranicza trasy do wymienionych w zleceniu — bez `/review`, `/conflicts`, `/transfers`,
 * `/backups`, `/admin/users` z pełnego katalogu §6 (kolejne zadania). `/trash` doszedł w etapie 6
 * razem z koszem (§14.2, A06, ADR 0012). `/review`/`/conflicts` doszły w etapie 7. `/transfers`
 * (import i eksport) doszedł w etapie 8 — patrz uzasadnienie doboru uprawnienia przy pozycji
 * niżej. `/admin` (panel administratora, §23 wiersz 11) doszedł w etapie 11 zadanie 1, bramkowanie
 * jego pozycji poszerzone w etapie 11 zadanie 2 (patrz komentarz przy tej pozycji niżej).
 *
 * `permission` odwołuje się do kodu z zamkniętego katalogu jedenastu uprawnień §12.1
 * (`apps/server/src/auth/permissions.ts`, `PERMISSION_CODES`) — pozycja bez `permission` jest
 * widoczna dla KAŻDEGO zalogowanego konta niezależnie od roli (dziś tylko „Ustawienia").
 *
 * §4: wszystkie cztery role (w tym `tylko_odczyt`) mają uprawnienie `read`
 * (`migrations/0002_seed_rbac_dictionaries.sql`) — filtrowanie wg `read` pokazuje Drzewo/Osoby/
 * Źródła/Badania/Historię każdemu aktywnemu kontu, zgodnie z macierzą §4 („Odczyt drzewa i
 * profili: Tak" dla wszystkich czterech ról).
 *
 * PRZYPOMNIENIE NIE DO POMINIĘCIA (§4 dosłownie): „uprawnienia egzekwowane w API, a nie tylko
 * przez ukrywanie przycisków". Cała ta lista — `permission` na każdej pozycji, funkcja
 * {@link filterNavItemsByPermissions} niżej — jest WYŁĄCZNIE WYGODĄ NAWIGACJI, nie mechanizmem
 * bezpieczeństwa: ukrycie linku nie chroni niczego, bo każda trasa i tak wymaga uprawnienia
 * niezależnie na serwerze (`requirePermission`/`requireFreshSession`,
 * `apps/server/src/auth/guards.ts`), a JavaScript klienta jest w pełni pod kontrolą osoby, która
 * go uruchamia — konto bez uprawnienia dostałoby 403 z API nawet gdyby ręcznie wywołało żądanie
 * z pominięciem tego menu. Traktowanie tej listy jako granicy dostępu byłoby błędem: to jest
 * WYŁĄCZNIE informacja „czy pokazać link", nigdy „czy wolno wykonać operację".
 */
export interface NavItem {
  to: string;
  label: string;
  /** Pojedyncze uprawnienie wymagane do pokazania pozycji, ALBO — od etapu 11 zadania 2 —
   * tablica uprawnień w relacji OR (pozycja widoczna, gdy konto ma DOWOLNE jedno z wymienionych).
   * Jedyna dziś pozycja korzystająca z tablicy to „Administracja" (patrz komentarz niżej) — każda
   * inna pozycja ma dokładnie jedno uprawnienie, bo macierz §4 daje im dokładnie jedną kolumnę
   * „Tak" wśród ról, którym to uprawnienie odpowiada. */
  permission?: string | readonly string[];
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/tree', label: 'Drzewo', permission: 'read' },
  { to: '/people', label: 'Osoby', permission: 'read' },
  { to: '/sources', label: 'Źródła', permission: 'read' },
  { to: '/research', label: 'Badania', permission: 'read' },
  // Recenzja i Konflikty wymagają `edit`, i to jest dobór z macierzy §4, nie ostrożność.
  //
  // NIE `review`: redaktor ma prawo ZGŁOSIĆ hipotezę tożsamości i konflikt (§4 „Zgłoszenie
  // hipotezy / konfliktu": Tak dla administratora, redaktora i recenzenta), choć rozstrzygnąć ich
  // nie może. Bramka `review` odcięłaby mu funkcję, do której ma prawo; sekcje wymagające
  // rozstrzygania gasną same w środku strony (ADR 0017).
  //
  // NIE `read`: ten sam wiersz §4 daje roli „tylko odczyt" **Nie**, a wiersz „Zatwierdzanie
  // propozycji" również **Nie**. Konto tylko do odczytu nie może na tych ekranach ZROBIĆ nic —
  // pokazanie mu ich prowadziłoby wyłącznie do stron bez żadnej dostępnej akcji, a przy okazji
  // eksponowałoby warstwę badawczą, którą A13 każe domyślnie ukrywać. `edit` mają dokładnie te
  // trzy role, które §4 wymienia w wierszu „Zgłoszenie hipotezy / konfliktu" (ADR 0007).
  { to: '/review', label: 'Recenzja', permission: 'edit' },
  { to: '/conflicts', label: 'Konflikty', permission: 'edit' },
  { to: '/history', label: 'Historia', permission: 'read' },
  // Kosz wymaga uprawnienia `edit`, nie `read` — konto tylko do odczytu nie przywraca
  // usuniętych encji (ADR 0012, decyzja o uprawnieniu; macierz §4).
  { to: '/trash', label: 'Kosz', permission: 'edit' },
  // `/transfers` (import i eksport, etap 8): bramkowane `export` — §4 daje DOKŁADNIE tę samą
  // trójkę ról (administrator/redaktor/recenzent) zarówno dla „Eksport JSON", jak i dla „Import:
  // upload, dry-run, mapowanie" (`transfer.preview`); `tylko_odczyt` ma „Nie" dla obu. Wybór
  // `export` zamiast `transfer.preview` jest arbitralny między tymi dwoma równoważnymi
  // uprawnieniami (oba dają identyczną widoczność pozycji nawigacji) — `TransfersRoute.tsx`
  // sprawdza OBA uprawnienia OSOBNO wewnątrz trasy (konto mogłoby teoretycznie mieć jedno bez
  // drugiego, §4: „konto może otrzymać kilka ról"), więc dobór tutaj wpływa wyłącznie na to, czy
  // pozycja menu w ogóle się pokazuje, nie na to, co trasa faktycznie udostępnia.
  { to: '/transfers', label: 'Import i eksport', permission: 'export' },
  // `/admin` (panel administratora, §23 wiersz 11, etap 11 zadania 1 i 2): bramkowane TRÓJKĄ
  // uprawnień w relacji OR — `backup` ALBO `users.manage` ALBO `audit.read`.
  //
  // HISTORIA DECYZJI (nie usuwać — zadanie 2 wprost każe „poszerzyć, zachowując czytelne
  // uzasadnienie w komentarzu"): zadanie 1 bramkowało WYŁĄCZNIE `backup`, bo wtedy jedyną
  // ZBUDOWANĄ zakładką panelu była „Kopie zapasowe" — pozostałe cztery były zaślepkami bez
  // żadnych danych, więc konto z `users.manage`/`audit.read` bez `backup` i tak nie zobaczyłoby w
  // panelu niczego poza zapowiedzią. Zadanie 2 domknęło zakładki „Konta" (`AccountsTab.tsx`,
  // wymaga `users.manage`) i „Audyt" (`AuditTab.tsx`, wymaga `audit.read`) na prawdziwym
  // backendzie — od teraz konto z JEDNYM z tych dwóch uprawnień, ale BEZ `backup`, ma w panelu
  // REALNĄ zdolność do wykonania. Macierz §4: „Użytkownicy i role" i „Audyt administracyjny i
  // logi techniczne" mają OBIE dokładnie jedną kolumnę „Tak" (administrator), ale to NIE
  // gwarantuje, że KAŻDE konto z uprawnieniem administracyjnym ma akurat `backup` — role są
  // przyznawane niezależnie („konto może otrzymać kilka ról"), więc dwie różne role mogłyby w
  // zasadzie kiedyś nieść `users.manage` i `backup` osobno. Zawężenie do samego `backup`
  // ukrywałoby dziś link menu przed kontem, które ma realną pracę do wykonania w zakładce Konta
  // albo Audyt — dokładnie błąd, przed którym ostrzega treść zlecenia zadania 2.
  //
  // Panel `/admin` jest JEDNĄ trasą z zakładkami (nie osobnymi linkami per zakładka) — jedna
  // pozycja nawigacji z trzema uprawnieniami w OR jest więc wierniejsza strukturze trasy niż
  // trzy osobne linki prowadzące do tego samego adresu.
  //
  // „Zadania" i „Importy" NIE dostały tu WŁASNEGO uprawnienia w tej trójce: `JobsTab.tsx` nie ma
  // JEDNEGO uprawnienia bramkującego całą zakładkę (autoryzacja jest per-zadanie, „właściciel LUB
  // uprawnienie z zamkniętej mapy rodzaju zadania", patrz komentarz w `AdminRoute.tsx`), a
  // `ImportsTab.tsx` to WYŁĄCZNIE odnośnik do `/transfers`, które ma WŁASNĄ bramkę
  // (`transfer.preview`/`transfer.apply`/`export`) niezależną od tego panelu — dodanie
  // `transfer.preview`/`export` do tej trójki pokazywałoby „Administrację" kontu redaktora bez
  // ŻADNEGO z pozostałych trzech uprawnień wyłącznie po to, żeby zobaczyć zakładkę „Zadania"
  // (dostępną każdemu zalogowanemu i tak) albo odnośnik do ekranu, do którego już ma osobny link
  // „Import i eksport" w tej samej nawigacji — rozszerzałoby widoczność linku bez odblokowania
  // żadnej NOWEJ zdolności.
  //
  // KAŻDA zakładka i tak sprawdza WŁASNE uprawnienie u siebie (`AdminRoute.tsx`,
  // `AccountsTab.tsx`, `AuditTab.tsx`) — ta pozycja menu pozostaje WYŁĄCZNIE wygodą nawigacji,
  // NIGDY granicą dostępu (patrz nagłówek pliku, PRZYPOMNIENIE NIE DO POMINIĘCIA). Konto z tylko
  // JEDNYM z trzech uprawnień widzi link, otwiera panel i widzi SWOJĄ zakładkę działającą, a
  // pozostałe pokazujące „brak uprawnienia" — nie przekierowanie, nie ukrycie całej strony.
  { to: '/admin', label: 'Administracja', permission: ['backup', 'users.manage', 'audit.read'] },
  { to: '/settings', label: 'Ustawienia' },
];

/** Pozycje widoczne od razu na dole ekranu telefonu (§6: „Drzewo, Szukaj, Osoby, Więcej") —
 * bez dedykowanego ekranu wyszukiwania w tym zadaniu (poza zamkniętym zakresem), więc pasek
 * telefonu pokazuje Drzewo/Osoby wprost, a reszta trafia pod „Więcej". */
export const MOBILE_PRIMARY_PATHS: readonly string[] = ['/tree', '/people'];

/** Pozycja bez `permission` jest widoczna zawsze; `permission` jako łańcuch wymaga DOKŁADNIE
 * tego uprawnienia; `permission` jako tablica (etap 11 zadanie 2, patrz komentarz przy pozycji
 * „Administracja" wyżej) wymaga DOWOLNEGO jednego uprawnienia z listy (OR, nie AND) — konto z
 * choćby jednym z wymienionych uprawnień widzi pozycję. */
export function filterNavItemsByPermissions(
  items: readonly NavItem[],
  permissions: readonly string[],
): NavItem[] {
  const permissionSet = new Set(permissions);
  return items.filter((item) => {
    if (item.permission === undefined) {
      return true;
    }
    if (typeof item.permission === 'string') {
      return permissionSet.has(item.permission);
    }
    return item.permission.some((required) => permissionSet.has(required));
  });
}
